import type { PublicUser, SyncPullResponse, SyncPushResponse } from '@malerbetrieb/shared';
import type { OutboxEntry } from '../offline/db.js';

/**
 * Schmaler HTTP-Zugang zum Backend.
 *
 * Es werden keine Tokens verwaltet – die Authentifizierung läuft über das
 * HttpOnly-Sitzungscookie, das der Browser automatisch mitsendet.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Sitzung abgelaufen oder nie vorhanden. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

/** Netzwerkfehler – kein Server erreicht, also vermutlich offline. */
export class NetworkError extends Error {
  constructor(message = 'Keine Verbindung zum Server.') {
    super(message);
    this.name = 'NetworkError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;

  try {
    response = await fetch(path, {
      credentials: 'same-origin',
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...init.headers,
      },
    });
  } catch {
    throw new NetworkError();
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await response.json() : null;

  if (!response.ok) {
    const error = body?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'UNKNOWN',
      error?.message ?? `Serverfehler (${response.status})`,
      error?.details,
    );
  }

  return body as T;
}

export const api = {
  // --- Auth ---------------------------------------------------------------
  login(username: string, password: string): Promise<{ user: PublicUser }> {
    return request('/api/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) });
  },

  logout(): Promise<{ ok: boolean }> {
    return request('/api/auth/logout', { method: 'POST' });
  },

  session(): Promise<{ user: PublicUser | null }> {
    return request('/api/auth/session');
  },

  changePassword(currentPassword: string, newPassword: string): Promise<{ ok: boolean }> {
    return request('/api/auth/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  // --- Synchronisation ----------------------------------------------------
  push(mutations: OutboxEntry[]): Promise<SyncPushResponse> {
    return request('/api/sync/push', {
      method: 'POST',
      body: JSON.stringify({
        mutations: mutations.map((entry) => ({
          mutationId: entry.id,
          entity: entry.entity,
          op: entry.op,
          id: entry.recordId,
          baseVersion: entry.baseVersion,
          payload: entry.payload,
          clientUpdatedAt: entry.clientUpdatedAt,
        })),
      }),
    });
  },

  pull(since: string, limit = 1000): Promise<SyncPullResponse> {
    return request(`/api/sync/pull?since=${encodeURIComponent(since)}&limit=${limit}`);
  },

  // --- Fotos --------------------------------------------------------------
  async uploadPhoto(
    photo: { id: string; orderId: string; positionId: string | null; note: string; width: number | null; height: number | null; createdAt: string },
    blob: Blob,
  ): Promise<{ photo: Record<string, unknown> }> {
    const form = new FormData();
    form.append('photoId', photo.id);
    form.append('orderId', photo.orderId);
    form.append('positionId', photo.positionId ?? 'null');
    form.append('note', photo.note);
    form.append('createdAt', photo.createdAt);
    if (photo.width) form.append('width', String(photo.width));
    if (photo.height) form.append('height', String(photo.height));
    form.append('file', blob, `${photo.id}.jpg`);

    return request('/api/photos', { method: 'POST', body: form });
  },

  photoUrl(photoId: string): string {
    return `/api/photos/${photoId}/file`;
  },
};
